import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components";
import { userManagementApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { UsersTable, type UserItem } from "@/pages/Users/UsersTable";
import { NewUserModal } from "@/pages/Users/NewUserModal";

export const Users: React.FC = () => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showError } = useToast();
    const [items, setItems] = useState<UserItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useStoredPreference(
        "vigepra_users_search",
        z.string().max(500),
        "",
        true
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false);

    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";

    const fetchItems = async (): Promise<void> => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const res = await userManagementApi.getAll();
            if (res.result) {
                setItems(res.data);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t("common.error");
            setErrorMessage(msg);
            showError(msg);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [activeCompanyId]);

    const handleUpdateItem = (updated: UserItem): void => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
            )
        );
    };

    const handleDeleteItem = (deletedId: number | string): void => {
        setItems((prev) => prev.filter((item) => item.id !== deletedId));
    };

    const displayedItems = items.filter((item) => {
        if (!isMainCompany && activeCompanyId) {
            const belongsToCompany = item.companies?.some(
                (c) => String(c.id) === String(activeCompanyId)
            );
            if (!belongsToCompany) {
                return false;
            }
        }

        if (search) {
            const s = search.toLowerCase();
            const name = item.name?.toLowerCase() || "";
            const email = item.email?.toLowerCase() || "";
            const rolesStr =
                !isMainCompany && item.roles
                    ? item.roles.map((r) => r.name.toLowerCase()).join(" ")
                    : "";
            const companiesStr =
                isMainCompany && item.companies
                    ? item.companies.map((c) => c.name.toLowerCase()).join(" ")
                    : "";

            if (
                !name.includes(s) &&
                !email.includes(s) &&
                !rolesStr.includes(s) &&
                !companiesStr.includes(s)
            ) {
                return false;
            }
        }
        return true;
    });

    return (
        <div className="page">
            <PageHeader
                compact
                className="!mb-0"
                title={<span>{t("users.title")}</span>}
                action={
                    isAdmin
                        ? {
                              label: t("users.new_button"),
                              icon: "plusCircle",
                              onClick: () => setIsNewModalOpen(true),
                          }
                        : null
                }
            />

            <UsersTable
                items={displayedItems}
                loading={loading}
                error={errorMessage}
                search={search}
                onSearchChange={setSearch}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onRefresh={fetchItems}
            />

            <NewUserModal
                isOpen={isNewModalOpen}
                onClose={() => setIsNewModalOpen(false)}
                onSuccess={() => fetchItems()}
            />
        </div>
    );
};
